import React from 'react';

export interface MatterportColorAdjustments {
  brightness?: number;
  contrast?: number;
  exposure?: number;
  highlights?: number;
  shadows?: number;
  temperature?: number;
  saturation?: number;
}

export const DEFAULT_ADJUSTMENTS: Required<MatterportColorAdjustments> = {
  brightness: 1,
  contrast: 1,
  exposure: 0,
  highlights: 0,
  shadows: 0,
  temperature: 0,
  saturation: 1
};

const TONE_CURVE_SAMPLE_COUNT = 33;

export function buildToneCurveSamples({
  shadows,
  highlights
}: {
  shadows: number;
  highlights: number;
}): number[] {
  const samples: number[] = [];
  for (let i = 0; i < TONE_CURVE_SAMPLE_COUNT; i++) {
    const x = i / (TONE_CURVE_SAMPLE_COUNT - 1);
    const shadowMask = Math.max(0, 1 - 2 * x);
    const highlightMask = Math.max(0, 2 * x - 1);
    const y = x + shadows * shadowMask * 0.5 + highlights * highlightMask * 0.5;
    samples.push(Math.max(0, Math.min(1, y)));
  }
  return samples;
}

function approxEqual(a: number, b: number, eps = 1e-4): boolean {
  return Math.abs(a - b) < eps;
}

function isDefault(adj: MatterportColorAdjustments): boolean {
  const merged = { ...DEFAULT_ADJUSTMENTS, ...adj };
  return (
    approxEqual(merged.brightness, 1) &&
    approxEqual(merged.contrast, 1) &&
    approxEqual(merged.exposure, 0) &&
    approxEqual(merged.highlights, 0) &&
    approxEqual(merged.shadows, 0) &&
    approxEqual(merged.temperature, 0) &&
    approxEqual(merged.saturation, 1)
  );
}

export interface MatterportColorFilter {
  filterId: string;
  filterElement: React.ReactElement;
}

export function buildMatterportColorFilter(
  adjustments: MatterportColorAdjustments,
  instanceKey: string
): MatterportColorFilter | null {
  if (isDefault(adjustments)) {
return null;
}

  const a = { ...DEFAULT_ADJUSTMENTS, ...adjustments };
  const filterId = `mp-color-${instanceKey}`;

  const totalGain = a.brightness * Math.pow(2, a.exposure);

  const contrastSlope = a.contrast;
  const contrastIntercept = (1 - a.contrast) / 2;

  const tR = 1 + 0.2 * a.temperature;
  const tG = 1 + 0.05 * a.temperature;
  const tB = 1 - 0.2 * a.temperature;

  const toneCurve = buildToneCurveSamples({
    shadows: a.shadows,
    highlights: a.highlights
  }).join(' ');

  const filterElement = (
    <svg
      aria-hidden="true"
      style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}
    >
      <filter id={filterId} colorInterpolationFilters="sRGB">
        <feComponentTransfer>
          <feFuncR type="linear" slope={totalGain} intercept={0} />
          <feFuncG type="linear" slope={totalGain} intercept={0} />
          <feFuncB type="linear" slope={totalGain} intercept={0} />
        </feComponentTransfer>
        <feColorMatrix
          type="matrix"
          values={`${tR} 0 0 0 0  0 ${tG} 0 0 0  0 0 ${tB} 0 0  0 0 0 1 0`}
        />
        <feComponentTransfer>
          <feFuncR type="linear" slope={contrastSlope} intercept={contrastIntercept} />
          <feFuncG type="linear" slope={contrastSlope} intercept={contrastIntercept} />
          <feFuncB type="linear" slope={contrastSlope} intercept={contrastIntercept} />
        </feComponentTransfer>
        <feComponentTransfer>
          <feFuncR type="table" tableValues={toneCurve} />
          <feFuncG type="table" tableValues={toneCurve} />
          <feFuncB type="table" tableValues={toneCurve} />
        </feComponentTransfer>
        <feColorMatrix type="saturate" values={String(a.saturation)} />
      </filter>
    </svg>
  );

  return { filterId, filterElement };
}
